'use client';
// src/components/DataModal.jsx
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
    FiX, FiDownloadCloud, FiUploadCloud, FiAlertTriangle, FiInfo, FiChevronDown
} from 'react-icons/fi';

const LS_PREFIX = 'energiberegner:';
const META_LS_KEY = `${LS_PREFIX}lastBackupAt`;

const POST_IMPORT_KEY = `${LS_PREFIX}postImportReport`;
const REOPEN_FLAG_KEY = `${LS_PREFIX}reopenDataModal`;

export default function DataModal({ open, onClose }) {
    const boxRef = useRef(null);
    const fileRef = useRef(null);

    const [busy, setBusy] = useState(false);
    const [lastBackup, setLastBackup] = useState('');
    const [message, setMessage] = useState('');
    const [report, setReport] = useState(null);

    const [forceOpen, setForceOpen] = useState(false);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        setLastBackup(readableDate(getLastBackupTs()));

        try {
            const shouldReopen = sessionStorage.getItem(REOPEN_FLAG_KEY) === '1';
            if (shouldReopen) {
                const raw = sessionStorage.getItem(POST_IMPORT_KEY);
                if (raw) {
                    const parsed = JSON.parse(raw);
                    setReport(parsed);
                }
                setMessage('Import gennemført. Projekter er opdateret/tilføjet efter behov.');
                setForceOpen(true);
            }
        } catch { }
    }, []);

    useEffect(() => {
        if (!open && !forceOpen) return;
        const onDown = e => { if (boxRef.current && !boxRef.current.contains(e.target)) handleClose(); };
        const onEsc = e => { if (e.key === 'Escape') handleClose(); };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('keydown', onEsc);
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('keydown', onEsc);
        };
    }, [open, forceOpen]);

    const handleClose = (href) => {
        try {
            sessionStorage.removeItem(REOPEN_FLAG_KEY);
            sessionStorage.removeItem(POST_IMPORT_KEY);
        } catch { }
        setForceOpen(false);
        onClose?.(href);
    };

    const doExport = async () => {
        try {
            setBusy(true);
            setMessage('');
            setReport(null);
            const payload = await exportAllAppData();
            const stamp = new Date().toISOString().replace(/[:.]/g, '-');
            const name = `energiberegner-backup-${stamp}.json`;
            const jsonText = JSON.stringify(payload, null, 2);

            if (typeof window !== 'undefined' && 'showSaveFilePicker' in window) {
                try {
                    const handle = await window.showSaveFilePicker({
                        suggestedName: name,
                        types: [{ description: 'JSON', accept: { 'application/json': ['.json'] } }],
                        excludeAcceptAllOption: false,
                    });
                    const writable = await handle.createWritable();
                    await writable.write(new Blob([jsonText], { type: 'application/json' }));
                    await writable.close();
                    setMessage('Sikkerhedskopi gemt.');
                } catch (err) {
                    if (!err || err.name !== 'AbortError') {
                        downloadJson(payload, name);
                        setMessage('Sikkerhedskopi downloadet (gem-dialog mislykkedes).');
                    }
                }
            } else {
                downloadJson(payload, name);
                setMessage('Sikkerhedskopi downloadet.');
            }

            safeSetLS(META_LS_KEY, String(Date.now()));
            setLastBackup(readableDate(Date.now()));
        } catch (err) {
            console.error(err);
            setMessage('Kunne ikke eksportere data. Se konsollen for detaljer.');
        } finally {
            setBusy(false);
        }
    };

    const onPickFile = () => fileRef.current?.click();

    const onImport = async e => {
        const file = e.target.files?.[0];
        if (!file) return;
        try {
            setBusy(true);
            setMessage('');
            setReport(null);

            const text = await file.text();
            const snapshot = JSON.parse(text);

            const { report } = await importAllAppData(snapshot, 'merge'); 
            try {
                sessionStorage.setItem(POST_IMPORT_KEY, JSON.stringify(report));
                sessionStorage.setItem(REOPEN_FLAG_KEY, '1');
            } catch { }

            safeSetLS(META_LS_KEY, String(Date.now()));

            location.reload();
        } catch (err) {
            console.error(err);
            setMessage('Kunne ikke importere filen. Er det en gyldig backup JSON?');
        } finally {
            e.target.value = '';
            setBusy(false);
        }
    };

    if (typeof window === 'undefined' || (!open && !forceOpen)) return null;

    return createPortal(
        <div style={backdropStyle}>
            <div ref={boxRef} style={modalStyle} role="dialog" aria-modal="true" aria-labelledby="dataModalTitle">
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <h2 id="dataModalTitle" style={{ margin: 0, flex: 1 }}>Data: backup & import</h2>
                    <button className="iconBtn" onClick={() => handleClose()} title="Luk"><FiX /></button>
                </div>

                <p style={{ marginTop: '.5rem', color: '#444' }}>
                    <FiInfo style={{ verticalAlign: '-2px' }} /> Denne app gemmer <b>kun lokalt i din browser</b>. Der er intet i skyen.
                    Hvis browserdata ryddes, kan dine projekter gå tabt. Lav derfor jævnligt en sikkerhedskopi.
                </p>

                <div style={warnBox}>
                    <FiAlertTriangle style={{ marginRight: 8 }} />
                    <div>
                        <div><b>Sidst sikkerhedskopieret:</b> {lastBackup || 'Aldrig'}</div>
                        <small style={{ color: '#555' }}>
                            Tip: gem filen et sted der bliver sikkerhedskopieret (ikke Downloads-mappen du aldrig sorterer).
                        </small>
                    </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '.25rem' }}>
                    <button
                        type="button"
                        className="addBtn"
                        onClick={doExport}
                        disabled={busy}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, height: '2.7rem' }}
                        title="Eksportér alle projekter og indstillinger til en JSON-fil"
                    >
                        <FiDownloadCloud /> Eksportér
                    </button>

                    <button
                        type="button"
                        onClick={onPickFile}
                        disabled={busy}
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, height: '2.7rem', border: '1px solid #bbb', borderRadius: 6 }}
                        title="Importér fra en tidligere sikkerhedskopi (JSON)"
                    >
                        <FiUploadCloud /> Importér
                    </button>
                </div>

                {message && <div style={{ marginTop: '.75rem', color: '#2e7d32' }}>{message}</div>}
                {report && <ImportResult report={report} />}

                <input
                    ref={fileRef}
                    type="file"
                    accept="application/json"
                    style={{ display: 'none' }}
                    onChange={onImport}
                />
            </div>
        </div>,
        document.body
    );
}


function ImportResult({ report }) {
    const {
        projects = { added: 0, updated: 0, skipped: 0, items: [] },
        lsKeysAdded = 0,
        lsKeysSkipped = 0,
        idbStoresWritten = 0,
    } = report || {};

    const totalProj = (projects.added || 0) + (projects.updated || 0) + (projects.skipped || 0);

    return (
        <div style={resultBox}>
            <div style={{ fontWeight: 700, marginBottom: 6 }}>Importresultat</div>
            <div style={countRow}>
                <span>Projekter</span>
                <b>{totalProj}</b>
            </div>
            {(projects.added || projects.updated || projects.skipped) > 0 && (
                <div style={subRow}>Tilføjet <b>{projects.added || 0}</b> • Opdateret <b>{projects.updated || 0}</b> • Uændret <b>{projects.skipped || 0}</b></div>
            )}

            <details style={{ marginTop: 8 }}>
                <summary style={summaryBtn}><FiChevronDown style={{ verticalAlign: '-2px' }} /> Vis projekter</summary>
                {projects.items?.length ? (
                    <div style={scrollList}>
                        {projects.items.map((p, i) => (
                            <div key={i} style={itemRow}>
                                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {p.name || '(uden navn)'}
                                </span>
                                <small style={{ color: '#666' }}>
                                    {p.mode}{p.updatedAt ? ` • ændret ${readableDate(p.updatedAt)}` : ''}
                                </small>
                            </div>
                        ))}
                    </div>
                ) : <small style={{ color: '#666' }}>Ingen projekter i backup.</small>}
            </details>

            <div style={{ fontSize: 12, color: '#555' }}>
                Tip: Genindlæs siden hvis projektlisten ikke opdaterer sig med det samme.
            </div>
        </div>
    );
}


function safeSetLS(k, v) {
    try { if (typeof window !== 'undefined') localStorage.setItem(k, v); } catch { }
}
function safeGetLS(k) {
    try { if (typeof window !== 'undefined') return localStorage.getItem(k); } catch { }
    return null;
}
function getLastBackupTs() {
    const v = safeGetLS(META_LS_KEY);
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
}
function readableDate(ts) {
    if (!ts) return '';
    try { return new Date(ts).toLocaleString('da-DK'); } catch { return ''; }
}

function downloadJson(obj, filename) {
    const blob = new Blob([JSON.stringify(obj, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 0);
}

async function exportAllAppData() {
    const meta = {
        app: 'energiberegneren',
        version: 2,
        createdAt: new Date().toISOString(),
        origin: typeof window !== 'undefined' ? location.origin : '',
    };

    const local = {};
    try {
        if (typeof window !== 'undefined' && window.localStorage) {
            for (let i = 0; i < localStorage.length; i++) {
                const k = localStorage.key(i);
                if (k && k.startsWith(LS_PREFIX)) local[k] = localStorage.getItem(k);
            }
        }
    } catch { /* blocked */ }

    const dbDump = {};
    if (typeof window !== 'undefined' && 'indexedDB' in window) {
        const dbs = await safeListDatabases();
        const ours = (dbs || []).filter(d => /energi|beregn|project/i.test(d?.name || ''));
        for (const d of ours) {
            const { name, version } = d;
            dbDump[name] = await dumpDbWithKeys(name, version);
        }
    }

    return { meta, localStorage: local, indexedDB: dbDump };
}

function safeListDatabases() {
    if (!indexedDB.databases) return Promise.resolve([]);
    try { return indexedDB.databases(); } catch { return Promise.resolve([]); }
}

async function dumpDbWithKeys(name, version) {
    return new Promise((resolve) => {
        const req = indexedDB.open(name, version);
        req.onerror = () => resolve({});
        req.onsuccess = async () => {
            const db = req.result;
            const out = {};
            try {
                const tx = db.transaction(db.objectStoreNames, 'readonly');
                for (const storeName of db.objectStoreNames) {
                    const store = tx.objectStore(storeName);
                    const keyPath = store.keyPath ?? null;
                    const autoIncrement = !!store.autoIncrement;
                    const { keys, values } = await getAllKeysAndValues(store);
                    out[storeName] = {
                        meta: { keyPath, autoIncrement },
                        rows: keys.map((key, i) => ({ key, value: values[i] })),
                    };
                }
            } catch {
                const tx = db.transaction(db.objectStoreNames, 'readonly');
                for (const storeName of db.objectStoreNames) {
                    const store = tx.objectStore(storeName);
                    const values = await getAllValues(store);
                    out[storeName] = { meta: { keyPath: null, autoIncrement: false }, rows: values.map(v => ({ key: null, value: v })) };
                }
            } finally {
                db.close();
                resolve(out);
            }
        };
    });
}

function getAllValues(store) {
    return new Promise((resolve) => {
        if ('getAll' in store) {
            const r = store.getAll();
            r.onsuccess = () => resolve(r.result || []);
            r.onerror = () => resolve([]);
        } else {
            const vals = [];
            const cur = store.openCursor();
            cur.onsuccess = (e) => {
                const c = e.target.result;
                if (c) { vals.push(c.value); c.continue(); } else resolve(vals);
            };
            cur.onerror = () => resolve(vals);
        }
    });
}
function getAllKeysAndValues(store) {
    return new Promise((resolve) => {
        const keys = [];
        const values = [];
        if ('getAllKeys' in store && 'getAll' in store) {
            const rk = store.getAllKeys();
            const rv = store.getAll();
            let kDone = false, vDone = false;
            rk.onsuccess = () => { kDone = true; if (vDone) resolve({ keys: rk.result || [], values: rv.result || [] }); };
            rv.onsuccess = () => { vDone = true; if (kDone) resolve({ keys: rk.result || [], values: rv.result || [] }); };
            rk.onerror = rv.onerror = () => resolve({ keys: [], values: [] });
        } else {
            const cur = store.openCursor();
            cur.onsuccess = (e) => {
                const c = e.target.result;
                if (c) { keys.push(c.key); values.push(c.value); c.continue(); } else resolve({ keys, values });
            };
            cur.onerror = () => resolve({ keys, values });
        }
    });
}


async function importAllAppData(snapshot, strategy = 'merge') {
    const report = {
        projects: { added: 0, updated: 0, skipped: 0, items: [] },
        lsKeysAdded: 0,
        lsKeysSkipped: 0,
        idbStoresWritten: 0,
    };

    try {
        if (typeof window !== 'undefined' && window.localStorage) {
            const src = snapshot?.localStorage && typeof snapshot.localStorage === 'object'
                ? snapshot.localStorage
                : {};

            for (const [key, val] of Object.entries(src)) {
                if (!String(key).startsWith(LS_PREFIX)) continue;
                const current = safeGetLS(key);

                const looksLikeProjectList = isProjectListJSON(val);

                if (strategy === 'replace') {
                    safeSetLS(key, val);
                    continue;
                }

                if (looksLikeProjectList) {
                    const { merged, added, updated, skipped, items } = mergeProjectListJSON(current, val);
                    safeSetLS(key, merged);
                    report.projects.added += added;
                    report.projects.updated += updated;
                    report.projects.skipped += skipped;
                    report.projects.items.push(...items);
                } else {
                    if (current == null) {
                        safeSetLS(key, val);
                        report.lsKeysAdded += 1;
                    } else {
                        report.lsKeysSkipped += 1;
                    }
                }
            }
        }
    } catch (e) {
        console.warn('localStorage import error:', e);
    }

    try {
        const dbs = snapshot?.indexedDB && typeof snapshot.indexedDB === 'object' ? snapshot.indexedDB : {};

        for (const [dbName, stores] of Object.entries(dbs)) {
            const db = await openDb(dbName);
            if (!db) continue;

            const wantedStores = Object.keys(stores || {});
            const missing = wantedStores.filter(s => !db.objectStoreNames.contains(s));
            db.close();

            if (missing.length) {
                const db2 = await upgradeDb(dbName, wantedStores, stores);
                db2?.close();
            }

            for (const [storeName, payload] of Object.entries(stores || {})) {
                const rows = normalizeRows(payload);

                const { added, updated, skipped, items } =
                    await mergeProjectsIntoStore(dbName, storeName, rows);

                report.projects.added += added;
                report.projects.updated += updated;
                report.projects.skipped += skipped;
                report.projects.items.push(...items);

                const wrote = await writeNonProjectRows(dbName, storeName, rows, strategy);
                report.idbStoresWritten += wrote ? 1 : 0;
            }
        }
    } catch (e) {
        console.warn('indexedDB import error:', e);
    }

    return { report };
}


function normalizeRows(storeDump) {
    if (storeDump && Array.isArray(storeDump.rows)) return storeDump.rows;
    if (Array.isArray(storeDump)) return storeDump.map(v => ({ key: null, value: v }));
    return [];
}

function openDb(name) {
    return new Promise((resolve) => {
        const r = indexedDB.open(name);
        r.onerror = () => resolve(null);
        r.onsuccess = () => resolve(r.result);
    });
}

function upgradeDb(name, wanted, storesDump) {
    return new Promise((resolve) => {
        const vProbe = indexedDB.open(name);
        vProbe.onerror = () => resolve(null);
        vProbe.onsuccess = () => {
            const curr = vProbe.result;
            const nextVersion = (curr.version || 1) + 1;
            curr.close();

            const up = indexedDB.open(name, nextVersion);
            up.onupgradeneeded = () => {
                const db = up.result;
                for (const s of wanted) {
                    if (!db.objectStoreNames.contains(s)) {
                        const meta = storesDump?.[s]?.meta || {};
                        if (meta && meta.keyPath) {
                            db.createObjectStore(s, { keyPath: meta.keyPath, autoIncrement: !!meta.autoIncrement });
                        } else {
                            db.createObjectStore(s);
                        }
                    }
                }
            };
            up.onerror = () => resolve(null);
            up.onsuccess = () => resolve(up.result);
        };
    });
}

async function mergeProjectsIntoStore(dbName, storeName, rows) {
    const out = { added: 0, updated: 0, skipped: 0, items: [] };

    const candidateArrays = rows
        .map(r => r.value)
        .filter(isProjectsArray);

    if (!candidateArrays.length) return out;

    const live = await readAllKeysAndValues(dbName, storeName);
    const liveIdx = live.values.findIndex(isProjectsArray);
    const liveKey = liveIdx >= 0 ? live.keys[liveIdx] : 'projects';
    const existing = liveIdx >= 0 ? live.values[liveIdx] : [];

    const incomingFlat = candidateArrays.flat();
    const { mergedArr, added, updated, skipped, items } =
        mergeProjectArrays(existing, incomingFlat);

    await putKV(dbName, storeName, mergedArr, liveKey);

    out.added += added; out.updated += updated; out.skipped += skipped;
    out.items.push(...items);
    return out;
}

async function writeNonProjectRows(dbName, storeName, rows, strategy) {
    const live = await readAllKeysAndValues(dbName, storeName);
    const liveKeySet = new Set(live.keys.map(k => keyToString(k)));

    const tx = await startRW(dbName, [storeName]);
    if (!tx) return false;
    const store = tx.objectStore(storeName);

    const puts = [];
    for (const { key, value } of rows) {
        if (isProjectsArray(value)) continue;

        if (strategy === 'replace') {
            if (store.keyPath) {
                puts.push(reqToPromise(store.put(value)));
            } else {
                puts.push(reqToPromise(store.put(value, key)));
            }
        } else {
            const has = key != null ? liveKeySet.has(keyToString(key)) : false;
            if (!has) {
                if (store.keyPath) puts.push(reqToPromise(store.put(value)));
                else puts.push(reqToPromise(store.put(value, key)));
            }
        }
    }

    await Promise.allSettled(puts);
    await txComplete(tx);
    return true;
}

function reqToPromise(req) {
    return new Promise((resolve) => {
        req.onsuccess = () => resolve(true);
        req.onerror = () => resolve(false);
    });
}

function keyToString(k) {
    try { return JSON.stringify(k); } catch { return String(k); }
}

function startRW(dbName, stores) {
    return new Promise(async (resolve) => {
        const db = await openDb(dbName);
        if (!db) return resolve(null);
        try {
            const tx = db.transaction(stores, 'readwrite');
            resolve(tx);
        } catch {
            db.close();
            resolve(null);
        }
    });
}
function txComplete(tx) {
    return new Promise((resolve) => {
        tx.oncomplete = () => { try { tx.db.close(); } catch { } resolve(true); };
        tx.onerror = () => { try { tx.db.close(); } catch { } resolve(false); };
        tx.onabort = () => { try { tx.db.close(); } catch { } resolve(false); };
    });
}

async function readAllKeysAndValues(dbName, storeName) {
    const db = await openDb(dbName);
    if (!db) return { keys: [], values: [] };
    let keys = [], values = [];
    try {
        const tx = db.transaction([storeName], 'readonly');
        const store = tx.objectStore(storeName);
        const res = await getAllKeysAndValues(store);
        keys = res.keys; values = res.values;
        await new Promise(r => { tx.oncomplete = r; tx.onerror = r; tx.onabort = r; });
    } catch { }
    db.close();
    return { keys, values };
}

async function putKV(dbName, storeName, value, key) {
    const tx = await startRW(dbName, [storeName]);
    if (!tx) return false;
    const store = tx.objectStore(storeName);
    const req = store.keyPath ? store.put(value) : store.put(value, key);
    await reqToPromise(req);
    await txComplete(tx);
    return true;
}


function isProjectsArray(v) {
    if (!Array.isArray(v)) return false;
    if (!v.length) return true;
    return v.every(o => o && typeof o === 'object' && 'name' in o && ('updatedAt' in o || 'createdAt' in o));
}
function isProjectListJSON(str) {
    try { return isProjectsArray(JSON.parse(str)); } catch { return false; }
}

function makeUniqueName(base, existingNames) {
    const taken = new Set([...existingNames].map(s => String(s).trim().toLowerCase()));
    const norm = s => String(s || '').trim().toLowerCase();

    if (!taken.has(norm(base))) return base;

    let candidate = `${base} (import)`;
    if (!taken.has(norm(candidate))) return candidate;

    for (let i = 2; i < 1000; i++) {
        candidate = `${base} (${i})`;
        if (!taken.has(norm(candidate))) return candidate;
    }
    return `${base} (import)`;
}

function mergeProjectArrays(existing, incoming) {
    const norm = s => String(s || '').trim().toLowerCase();

    const byName = new Map(existing.map(p => [norm(p.name), { ...p }]));
    const existingNames = new Set(existing.map(p => p.name));

    let added = 0, updated = 0, skipped = 0;
    const items = [];

    for (const p of incoming) {
        const key = norm(p.name);
        if (!key) continue;

        const cur = byName.get(key);

        if (!cur) {
            byName.set(key, p);
            existingNames.add(p.name);
            added++;
            items.push({ name: p.name, updatedAt: p.updatedAt || p.createdAt || null, mode: 'tilføjet' });
            continue;
        }

        const oldTs = Number(cur?.updatedAt || cur?.createdAt || 0);
        const newTs = Number(p?.updatedAt || p?.createdAt || 0);

        if (Number.isFinite(newTs) && newTs > oldTs) {
            byName.set(key, { ...cur, ...p });
            updated++;
            items.push({ name: p.name, updatedAt: p.updatedAt || p.createdAt || null, mode: 'opdateret' });
        } else if (!Number.isFinite(newTs) || newTs === oldTs) {
            const unique = makeUniqueName(p.name, existingNames);
            const copy = { ...p, name: unique };
            byName.set(norm(unique), copy);
            existingNames.add(unique);
            added++;
            items.push({ name: unique, updatedAt: copy.updatedAt || copy.createdAt || null, mode: 'tilføjet' });
        } else {
            skipped++;
            items.push({ name: p.name, updatedAt: cur?.updatedAt || cur?.createdAt || null, mode: 'uændret' });
        }
    }

    return { mergedArr: Array.from(byName.values()), added, updated, skipped, items };
}

function mergeProjectListJSON(currentStr, incomingStr) {
    let curr = [];
    let inc = [];
    try { curr = JSON.parse(currentStr || '[]') || []; } catch { curr = []; }
    try { inc = JSON.parse(incomingStr || '[]') || []; } catch { inc = []; }
    const { mergedArr, added, updated, skipped, items } = mergeProjectArrays(curr, inc);
    return { merged: JSON.stringify(mergedArr), added, updated, skipped, items };
}

/* ---------------- styles ---------------- */
const backdropStyle = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 5010
};
const modalStyle = {
    background: '#fff', borderRadius: 8, padding: '1.2rem 1.3rem 1.1rem',
    width: 'min(560px,92%)', maxHeight: '90vh', overflowY: 'auto',
    boxShadow: '0 8px 22px rgba(0,0,0,.15)', display: 'flex', flexDirection: 'column', gap: '.9rem'
};
const warnBox = {
    display: 'flex', alignItems: 'flex-start', gap: '.4rem',
    padding: '.6rem .7rem', border: '1px solid #e0b300', background: '#fff8e1', borderRadius: 6,
};
const resultBox = {
    marginTop: '.8rem',
    border: '1px solid #d9e5f8',
    background: '#f6f9ff',
    borderRadius: 6,
    padding: '.6rem .7rem'
};

const countRow = { display: 'flex', justifyContent: 'space-between' };
const subRow = { marginTop: 2, fontSize: 13, color: '#333' };
const summaryBtn = { cursor: 'pointer', userSelect: 'none', display: 'inline-flex', alignItems: 'center', gap: 6 };
const scrollList = { maxHeight: 160, overflow: 'auto', border: '1px solid #e7edf9', background: '#fff', borderRadius: 6, marginTop: 6 };
const itemRow = { display: 'flex', gap: 8, padding: '.35rem .5rem', borderBottom: '1px solid #f1f4fb' };
